import React, { useState, useEffect } from 'react';
import API_URL from '../../config/api';

const ClausulasManagement = () => {
  const [clausulas, setClausulas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingClausula, setEditingClausula] = useState(null);
  const [formData, setFormData] = useState({
    texto: '',
    categoria: '',
    ativa: true
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategoria, setFilterCategoria] = useState('');
  const [filterAtiva, setFilterAtiva] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  useEffect(() => {
    fetchClausulas();
  }, []);

  const fetchClausulas = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/clausulas`);
      const data = await response.json();
      
      if (data.clausulas) {
        setClausulas(data.clausulas);
      }
    } catch (error) {
      console.error('Erro ao buscar cláusulas:', error);
      alert('Erro ao carregar cláusulas');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.texto.trim()) {
      alert('O texto da cláusula é obrigatório');
      return;
    }

    try {
      const url = editingClausula
        ? `${API_URL}/api/clausulas/${editingClausula.id}`
        : `${API_URL}/api/clausulas`;
      
      const method = editingClausula ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        alert(data.message || (editingClausula ? 'Cláusula atualizada com sucesso!' : 'Cláusula criada com sucesso!'));
        setShowModal(false);
        resetForm();
        fetchClausulas();
      } else {
        alert(data.message || 'Erro ao salvar cláusula');
      }
    } catch (error) {
      console.error('Erro ao salvar cláusula:', error);
      alert('Erro ao salvar cláusula');
    }
  };

  const handleEdit = (clausula) => {
    setEditingClausula(clausula);
    setFormData({
      texto: clausula.texto,
      categoria: clausula.categoria || '',
      ativa: clausula.ativa !== false
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta cláusula?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/clausulas/${id}`, {
        method: 'DELETE',
      });

      const data = await response.json();

      if (response.ok) {
        alert(data.message || 'Cláusula excluída com sucesso!');
        fetchClausulas();
      } else {
        alert(data.message || 'Erro ao excluir cláusula');
      }
    } catch (error) {
      console.error('Erro ao excluir cláusula:', error);
      alert('Erro ao excluir cláusula');
    }
  };

  const handleToggleAtiva = async (clausula) => {
    try {
      const response = await fetch(`${API_URL}/api/clausulas/${clausula.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          texto: clausula.texto,
          categoria: clausula.categoria,
          ativa: !clausula.ativa
        }),
      });

      const data = await response.json();

      if (response.ok) {
        fetchClausulas();
      } else {
        alert(data.message || 'Erro ao atualizar status da cláusula');
      }
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      alert('Erro ao atualizar status da cláusula');
    }
  };

  const resetForm = () => {
    setFormData({
      texto: '',
      categoria: '',
      ativa: true
    });
    setEditingClausula(null);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  // Filtrar cláusulas
  const filteredClausulas = clausulas.filter(clausula => {
    const matchesSearch = clausula.texto.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (clausula.categoria && clausula.categoria.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategoria = !filterCategoria || clausula.categoria === filterCategoria;
    const matchesAtiva = filterAtiva === 'all' || 
                        (filterAtiva === 'ativa' && clausula.ativa !== false) ||
                        (filterAtiva === 'inativa' && clausula.ativa === false);
    
    return matchesSearch && matchesCategoria && matchesAtiva;
  });

  // Paginação
  const totalPages = Math.ceil(filteredClausulas.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedClausulas = filteredClausulas.slice(startIndex, endIndex);

  // Reset para primeira página quando filtros mudarem
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterCategoria, filterAtiva]);

  // Obter categorias únicas
  const categorias = [...new Set(clausulas.map(c => c.categoria).filter(Boolean))];

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Gerenciamento de Cláusulas</h1>
              <p className="text-gray-600 mt-1">Gerencie as cláusulas contratuais dos orçamentos</p>
            </div>
            <button
              onClick={() => setShowModal(true)}
              className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
              </svg>
              Nova Cláusula
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Buscar
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar por texto ou categoria..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Categoria
              </label>
              <select
                value={filterCategoria}
                onChange={(e) => setFilterCategoria(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="">Todas as categorias</option>
                {categorias.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                value={filterAtiva}
                onChange={(e) => setFilterAtiva(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="all">Todas</option>
                <option value="ativa">Ativas</option>
                <option value="inativa">Inativas</option>
              </select>
            </div>
          </div>
        </div>

        {/* Clausulas List */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
              <p className="mt-2 text-gray-600">Carregando cláusulas...</p>
            </div>
          ) : filteredClausulas.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto mb-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p>Nenhuma cláusula encontrada</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Texto da Cláusula
                      </th>
                      <th className="hidden md:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Categoria
                      </th>
                      <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-3 md:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedClausulas.map((clausula) => (
                      <tr key={clausula.id} className="hover:bg-gray-50">
                        <td className="px-3 md:px-6 py-4 text-sm text-gray-900">
                          <div className="max-w-2xl">
                            {clausula.texto}
                            {/* Mostrar categoria em mobile */}
                            <div className="md:hidden mt-1 text-xs text-gray-500">
                              {clausula.categoria && `Categoria: ${clausula.categoria}`}
                            </div>
                          </div>
                        </td>
                        <td className="hidden md:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {clausula.categoria || '-'}
                        </td>
                        <td className="px-3 md:px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => handleToggleAtiva(clausula)}
                            className={`px-2 md:px-3 py-1 rounded-full text-xs font-medium ${
                              clausula.ativa !== false
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {clausula.ativa !== false ? 'Ativa' : 'Inativa'}
                          </button>
                        </td>
                        <td className="px-3 md:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => handleEdit(clausula)}
                            className="text-blue-600 hover:text-blue-900 mr-2 md:mr-4"
                            title="Editar"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 inline" viewBox="0 0 20 20" fill="currentColor">
                              <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDelete(clausula.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Excluir"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 inline" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Paginação */}
              {filteredClausulas.length > 0 && (
                <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 sm:px-6">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                    {/* Items per page */}
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-gray-700">Itens por página:</label>
                      <select
                        value={itemsPerPage}
                        onChange={(e) => {
                          setItemsPerPage(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                        className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={30}>30</option>
                      </select>
                    </div>

                    {/* Page info */}
                    <div className="text-sm text-gray-700">
                      Mostrando <span className="font-medium">{startIndex + 1}</span> a{' '}
                      <span className="font-medium">{Math.min(endIndex, filteredClausulas.length)}</span> de{' '}
                      <span className="font-medium">{filteredClausulas.length}</span> resultados
                    </div>

                    {/* Pagination buttons */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className={`px-3 py-1 rounded-lg text-sm font-medium ${
                          currentPage === 1
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                        }`}
                      >
                        Anterior
                      </button>
                      
                      {/* Page numbers */}
                      <div className="hidden sm:flex gap-1">
                        {[...Array(totalPages)].map((_, idx) => {
                          const pageNum = idx + 1;
                          // Mostrar apenas páginas próximas
                          if (
                            pageNum === 1 ||
                            pageNum === totalPages ||
                            (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                          ) {
                            return (
                              <button
                                key={pageNum}
                                onClick={() => setCurrentPage(pageNum)}
                                className={`px-3 py-1 rounded-lg text-sm font-medium ${
                                  currentPage === pageNum
                                    ? 'bg-green-600 text-white'
                                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                                }`}
                              >
                                {pageNum}
                              </button>
                            );
                          } else if (
                            pageNum === currentPage - 2 ||
                            pageNum === currentPage + 2
                          ) {
                            return <span key={pageNum} className="px-2 py-1 text-gray-400">...</span>;
                          }
                          return null;
                        })}
                      </div>

                      {/* Mobile: apenas número da página atual */}
                      <div className="sm:hidden px-3 py-1 bg-white border border-gray-300 rounded-lg text-sm">
                        {currentPage} / {totalPages}
                      </div>

                      <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className={`px-3 py-1 rounded-lg text-sm font-medium ${
                          currentPage === totalPages
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                        }`}
                      >
                        Próxima
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Summary */}
        <div className="mt-6 bg-white rounded-lg shadow-sm p-4">
          <div className="flex flex-wrap gap-4 md:gap-6 text-sm text-gray-600">
            <div>
              <span className="font-medium">Total:</span> {clausulas.length} cláusulas
            </div>
            <div>
              <span className="font-medium">Ativas:</span> {clausulas.filter(c => c.ativa !== false).length}
            </div>
            <div>
              <span className="font-medium">Inativas:</span> {clausulas.filter(c => c.ativa === false).length}
            </div>
            <div>
              <span className="font-medium">Filtradas:</span> {filteredClausulas.length}
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-900">
                  {editingClausula ? 'Editar Cláusula' : 'Nova Cláusula'}
                </h2>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Texto da Cláusula *
                    </label>
                    <textarea
                      value={formData.texto}
                      onChange={(e) => setFormData({ ...formData, texto: e.target.value })}
                      rows={6}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="Digite o texto completo da cláusula..."
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Categoria
                    </label>
                    <input
                      type="text"
                      value={formData.categoria}
                      onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="Ex: Pagamento, Entrega, Garantia..."
                      list="categorias-list"
                    />
                    <datalist id="categorias-list">
                      {categorias.map(cat => (
                        <option key={cat} value={cat} />
                      ))}
                    </datalist>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="ativa"
                      checked={formData.ativa}
                      onChange={(e) => setFormData({ ...formData, ativa: e.target.checked })}
                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                    />
                    <label htmlFor="ativa" className="ml-2 block text-sm text-gray-900">
                      Cláusula ativa (será exibida nos orçamentos)
                    </label>
                  </div>
                </div>

                <div className="mt-6 flex gap-3 justify-end">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
                  >
                    {editingClausula ? 'Atualizar' : 'Criar'} Cláusula
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClausulasManagement;
